import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getRecipeById, forkRecipe, generateRecipeWithAI, generateRecipeWithText } from '../services/api';
import { useAuth } from '../context/authContext';
import { PageHeading, Heading, Text, Section, Modal } from '../components/ui';
import SignedImage from '../components/SignedImage';
import TimerIcon from '../components/ui/TimerIcon';
import RecipeComponentEditor, { AssemblyEditor } from '../components/RecipeComponentEditor';
import Toast from '../components/Toast';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    notecardBlue: '#4A90E2', // Blue color for notecard dividing lines
    red: '#DC2626', // Red color for title separator
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// TimerEditModal component - Now handles active/inactive timers
function TimerEditModal({ open, onClose, onSave, onRemove, stepIndex, timingSegments = [] }) {
    // Parse timing segments if they're a string
    let segments = timingSegments;
    if (typeof timingSegments === 'string') {
        try {
            segments = JSON.parse(timingSegments);
        } catch (e) {
            segments = [];
        }
    }
    
    // Ensure segments is always an array
    if (!Array.isArray(segments)) {
        segments = [];
    }

    // Ensure we have both active and inactive
    const activeSegment = segments.find(s => s.kind === 'active') || {
        kind: 'active',
        label: 'Active time',
        expectedSeconds: 0
    };
    const inactiveSegment = segments.find(s => s.kind === 'inactive') || {
        kind: 'inactive',
        label: 'Inactive time',
        expectedSeconds: 0
    };

    const [activeMinutes, setActiveMinutes] = useState(Math.floor(activeSegment.expectedSeconds / 60));
    const [activeSeconds, setActiveSeconds] = useState(activeSegment.expectedSeconds % 60);
    const [activeLabel, setActiveLabel] = useState(activeSegment.label || 'Active time');
    
    const [inactiveMinutes, setInactiveMinutes] = useState(Math.floor(inactiveSegment.expectedSeconds / 60));
    const [inactiveSeconds, setInactiveSeconds] = useState(inactiveSegment.expectedSeconds % 60);
    const [inactiveLabel, setInactiveLabel] = useState(inactiveSegment.label || 'Inactive time');

    useEffect(() => {
        if (open) {
            // Re-parse segments in case timingSegments changed
            let currentSegments = timingSegments;
            if (typeof timingSegments === 'string') {
                try {
                    currentSegments = JSON.parse(timingSegments);
                } catch (e) {
                    currentSegments = [];
                }
            }
            if (!Array.isArray(currentSegments)) {
                currentSegments = [];
            }
            
            const active = currentSegments.find(s => s.kind === 'active') || { expectedSeconds: 0, label: 'Active time' };
            const inactive = currentSegments.find(s => s.kind === 'inactive') || { expectedSeconds: 0, label: 'Inactive time' };
            
            setActiveMinutes(Math.floor(active.expectedSeconds / 60));
            setActiveSeconds(active.expectedSeconds % 60);
            setActiveLabel(active.label || 'Active time');
            
            setInactiveMinutes(Math.floor(inactive.expectedSeconds / 60));
            setInactiveSeconds(inactive.expectedSeconds % 60);
            setInactiveLabel(inactive.label || 'Inactive time');
        }
    }, [open, timingSegments]);

    const handleSave = () => {
        const activeTotalSeconds = activeMinutes * 60 + activeSeconds;
        const inactiveTotalSeconds = inactiveMinutes * 60 + inactiveSeconds;
        
        const newSegments = [
            {
                kind: 'active',
                label: activeLabel,
                expectedSeconds: activeTotalSeconds,
                order: 0
            },
            {
                kind: 'inactive',
                label: inactiveLabel,
                expectedSeconds: inactiveTotalSeconds,
                order: 1
            }
        ];
        
        onSave(newSegments);
    };

    if (!open) return null;
    
    const totalMinutes = activeMinutes + inactiveMinutes;
    const totalSeconds = activeSeconds + inactiveSeconds;
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px] max-w-[90vw]">
                <h2 className="font-mono text-lg mb-4">Edit Timers for Step {stepIndex + 1}</h2>
                
                {/* Active Timer (Red) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#991b1b' }}>ACTIVE TIME</label>
                    <input
                        type="text"
                        value={activeLabel}
                        onChange={e => setActiveLabel(e.target.value)}
                        placeholder="e.g., Whisking to stiff peaks"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={activeMinutes}
                                onChange={e => setActiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={activeSeconds}
                                onChange={e => setActiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Inactive Timer (Blue) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#1e40af' }}>INACTIVE TIME</label>
                    <input
                        type="text"
                        value={inactiveLabel}
                        onChange={e => setInactiveLabel(e.target.value)}
                        placeholder="e.g., Baking until golden brown"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={inactiveMinutes}
                                onChange={e => setInactiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={inactiveSeconds}
                                onChange={e => setInactiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Total Duration Display */}
                <div className="mb-4 p-2 bg-neutral-50 rounded text-center">
                    <span className="font-mono text-xs text-neutral-600">Total: {totalMinutes}m {totalSeconds}s</span>
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        className="px-4 py-2 bg-neutral-200 text-neutral-900 rounded font-mono text-xs hover:bg-neutral-300"
                        onClick={onClose}
                    >Cancel</button>
                    {onRemove && (
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded font-mono text-xs hover:bg-red-700"
                            onClick={onRemove}
                        >Remove</button>
                    )}
                    <button
                        className="px-4 py-2 bg-neutral-900 text-white rounded font-mono text-xs hover:bg-neutral-800"
                        onClick={handleSave}
                    >Save</button>
                </div>
            </div>
        </div>
    );
}

const ForkRecipe = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        cooking_time: '',
        // Multi-component flag
        isMultiComponent: false,
        // Simple mode fields
        ingredients: [''],
        prerequisites: [''],
        steps: [''],
        // Multi-component mode fields
        components: [],
        assembly: { prerequisites: [''], steps: [''] },
        // Other fields
        images: [],
    });
    const [timerModal, setTimerModal] = useState({ open: false, stepIndex: null, timingSegments: null });
    const [stepTimingSegments, setStepTimingSegments] = useState({}); // Store timing_segments for each step
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalRecipe, setOriginalRecipe] = useState(null);
    const [saving, setSaving] = useState(false);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // AI Context Generation State
    const [transferMode, setTransferMode] = useState('url'); // 'url' or 'text'
    const [urlContext, setUrlContext] = useState('');
    const [textContent, setTextContent] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiGenerated, setAiGenerated] = useState(false);
    const [highlightFields, setHighlightFields] = useState({});
    const [progressData, setProgressData] = useState({ step: '', message: '', progress: 0 });
    const [sourceUrl, setSourceUrl] = useState('');
    const [showAuthorModal, setShowAuthorModal] = useState(false);
    const [manualAuthor, setManualAuthor] = useState('');
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
    const [showPasteAttributionModal, setShowPasteAttributionModal] = useState(false);
    const [pasteSourceUrl, setPasteSourceUrl] = useState('');
    const [pasteAuthor, setPasteAuthor] = useState('');
    
    // Pre-transfer acknowledgment state
    const [acknowledgmentAccepted, setAcknowledgmentAccepted] = useState(false);

    const categories = [
        'Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Snack', 
        'Dessert', 'Supper', 'Appetizer', 'Side', 'Drink', 
        'Condiment', 'Bread', 'Salad', 'Soup'
    ];

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Reset acknowledgment when transfer mode changes
    useEffect(() => {
        setAcknowledgmentAccepted(false);
    }, [transferMode]);

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                setLoading(true);
                const data = await getRecipeById(id);
                
                // Log recipe fetch for debugging
                console.log('🔍 ForkRecipe: Fetched recipe for forking:', {
                    requestedId: id,
                    returnedId: data.id,
                    recipeName: data.name,
                    imageCount: Array.isArray(data.images) ? data.images.length : 0,
                    images: data.images,
                    chain_root_id: data.chain_root_id,
                    parent_recipe_id: data.parent_recipe_id,
                    note: 'Images should be specific to this recipe only'
                });
                
                setOriginalRecipe(data);

                // Ensure ingredients is an array
                let ingredients = [];
                if (typeof data.ingredients === 'string') {
                    ingredients = JSON.parse(data.ingredients);
                } else if (Array.isArray(data.ingredients)) {
                    ingredients = data.ingredients;
                }

                // Ensure instructions are parsed if necessary
                let instructions = data.instructions || {};
                if (typeof instructions === 'string') {
                    instructions = JSON.parse(instructions);
                }

                // Ensure prerequisites and steps are arrays
                const prerequisites = Array.isArray(instructions.prerequisites)
                    ? instructions.prerequisites
                    : instructions.prerequisites ? instructions.prerequisites.split('\n') : [''];
                
                const steps = Array.isArray(instructions.steps)
                    ? instructions.steps
                    : instructions.steps ? instructions.steps.split('\n') : [''];

                // Load timing_segments from steps_with_timing if available (preferred method)
                // Otherwise, fall back to generating from timer_associations
                const initialTimingSegments = {};
                
                if (data.steps_with_timing && Array.isArray(data.steps_with_timing) && data.steps_with_timing.length > 0) {
                    // Use timing_segments from steps_with_timing (preserves active/inactive timers)
                    data.steps_with_timing.forEach((stepWithTiming, index) => {
                        if (stepWithTiming.timing_segments && Array.isArray(stepWithTiming.timing_segments)) {
                            // Ensure we have both active and inactive timers
                            const segments = stepWithTiming.timing_segments;
                            const hasActive = segments.some(s => s.kind === 'active');
                            const hasInactive = segments.some(s => s.kind === 'inactive');
                            
                            if (hasActive && hasInactive) {
                                initialTimingSegments[index] = segments;
                            } else {
                                // Normalize to ensure both exist
                                const active = segments.find(s => s.kind === 'active') || {
                                    kind: 'active',
                                    label: 'Active time',
                                    expectedSeconds: 0,
                                    order: 0
                                };
                                const inactive = segments.find(s => s.kind === 'inactive') || {
                                    kind: 'inactive',
                                    label: 'No waiting required',
                                    expectedSeconds: 0,
                                    order: 1
                                };
                                initialTimingSegments[index] = [active, inactive];
                            }
                        }
                    });
                } else {
                    // Fallback: Parse timer associations and convert to timing_segments
                    let timerAssociations = [];
                    if (data.timer_associations) {
                        try {
                            timerAssociations = typeof data.timer_associations === 'string' 
                                ? JSON.parse(data.timer_associations) 
                                : data.timer_associations;
                        } catch (parseError) {
                            console.error('Error parsing timer associations:', parseError);
                            timerAssociations = [];
                        }
                    }
                    
                    // Convert timer_associations to timing_segments format
                    steps.forEach((_, index) => {
                        const ta = timerAssociations.find(t => t.stepIndex === index);
                        if (ta && ta.duration) {
                            // Convert duration (in minutes) to seconds and create timing_segments
                            const durationSeconds = ta.duration * 60;
                            initialTimingSegments[index] = [
                                {
                                    kind: 'active',
                                    label: ta.description || `Step ${index + 1} active time`,
                                    expectedSeconds: durationSeconds,
                                    order: 0
                                },
                                {
                                    kind: 'inactive',
                                    label: 'No waiting required',
                                    expectedSeconds: 0,
                                    order: 1
                                }
                            ];
                        }
                    });
                }
                setStepTimingSegments(initialTimingSegments);

                // Check if this is a multi-component recipe
                const isMultiComponent = data.isMultiComponent === true;
                
                // Parse components if available
                let components = [];
                if (data.components) {
                    try {
                        components = typeof data.components === 'string' 
                            ? JSON.parse(data.components) 
                            : data.components;
                    } catch (e) {
                        console.error('Error parsing components:', e);
                        components = [];
                    }
                }
                
                // Parse assembly if available
                let assembly = { prerequisites: [''], steps: [''] };
                if (data.assembly) {
                    try {
                        assembly = typeof data.assembly === 'string' 
                            ? JSON.parse(data.assembly) 
                            : data.assembly;
                        assembly.prerequisites = assembly.prerequisites || [''];
                        assembly.steps = assembly.steps || [''];
                    } catch (e) {
                        console.error('Error parsing assembly:', e);
                    }
                }

                setFormData({
                    name: data.name || '',
                    category: data.category || '',
                    cooking_time: data.cooking_time || '',
                    isMultiComponent: isMultiComponent,
                    ingredients: ingredients.length > 0 ? ingredients : [''],
                    prerequisites: prerequisites.length > 0 ? prerequisites : [''],
                    steps: steps.length > 0 ? steps : [''],
                    components: Array.isArray(components) && components.length > 0 
                        ? components 
                        : [],
                    assembly: assembly,
                    images: [],
                });
                setExistingImages(data.images || []);
            } catch (err) {
                console.error('Error fetching recipe:', err);
                setError('Failed to load recipe for forking.');
            } finally {
                setLoading(false);
            }
        };
        fetchRecipe();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleListChange = (listName, index, value) => {
        setFormData(prev => ({
            ...prev,
            [listName]: prev[listName].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (listName) => {
        setFormData(prev => ({
            ...prev,
            [listName]: [...prev[listName], '']
        }));
    };

    const removeListItem = (listName, index) => {
        // Allow deletion of prerequisites even when there's only one
        if (listName === 'prerequisites' || formData[listName].length > 1) {
            setFormData(prev => ({
                ...prev,
                [listName]: prev[listName].filter((_, i) => i !== index)
            }));
        }
    };

    // Multi-component handlers
    const toggleMultiComponent = () => {
        setFormData(prev => ({
            ...prev,
            isMultiComponent: !prev.isMultiComponent,
            components: !prev.isMultiComponent && prev.components.length === 0 
                ? [{ 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }] 
                : prev.components
        }));
    };

    const addComponent = () => {
        setFormData(prev => ({
            ...prev,
            components: [
                ...prev.components,
                { 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }
            ]
        }));
    };

    const updateComponent = (index, updatedComponent) => {
        setFormData(prev => ({
            ...prev,
            components: prev.components.map((comp, i) => 
                i === index ? { ...comp, ...updatedComponent } : comp
            )
        }));
    };

    const removeComponent = (index) => {
        if (formData.components.length > 1) {
            setFormData(prev => ({
                ...prev,
                components: prev.components.filter((_, i) => i !== index)
            }));
        }
    };

    const updateAssembly = (newAssembly) => {
        setFormData(prev => ({
            ...prev,
            assembly: newAssembly
        }));
    };

    const handleImageUpload = (files) => {
        const fileArray = Array.from(files);
        
        // Validate files
        const validFiles = [];
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        
        fileArray.forEach(file => {
            if (!file.type.startsWith('image/')) {
                setError(`File "${file.name}" is not an image. Please select only image files.`);
                return;
            }
            if (file.size > maxFileSize) {
                setError(`File "${file.name}" is too large. Please select files smaller than 10MB.`);
                return;
            }
            validFiles.push(file);
        });
        
        if (validFiles.length === 0) return;
        
        setImageFiles(prev => [...prev, ...validFiles]);
        
        // Create previews
        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreviews(prev => [...prev, e.target.result]);
            };
            reader.onerror = () => {
                setError(`Failed to read file "${file.name}". Please try again.`);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileInput = (e) => {
        handleImageUpload(e.target.files);
        e.target.value = ''; // Clear the input so the same file can be selected again
    };

    const triggerFileInput = () => {
        const input = document.getElementById('fork-image-input');
        if (input) {
            input.click();
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleImageUpload(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const removeImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingImage = (imgUrl) => {
        setExistingImages(prev => prev.filter(img => img !== imgUrl));
    };

    const nextImage = () => {
        const allImages = [...existingImages, ...imagePreviews];
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
        }
    };

    const prevImage = () => {
        const allImages = [...existingImages, ...imagePreviews];
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
        }
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    const startEditing = (field) => {
        setEditingField(field);
    };

    const stopEditing = () => {
        setEditingField(null);
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const { name, category, cooking_time, isMultiComponent, ingredients, prerequisites, steps, components, assembly } = formData;

        // Basic validation
        if (!name.trim()) {
            setError('Recipe name is required.');
            return;
        }
        if (!category) {
            setError('Please select a category.');
            return;
        }
        if (!cooking_time || cooking_time <= 0) {
            setError('Please enter a valid cooking time.');
            return;
        }
        
        // Validation differs based on isMultiComponent flag
        if (isMultiComponent) {
            // Multi-component validation
            if (!components || components.length === 0) {
                setError('Please add at least one component.');
                return;
            }
            
            for (let i = 0; i < components.length; i++) {
                const comp = components[i];
                if (!comp.name || !comp.name.trim()) {
                    setError(`Component ${i + 1} needs a name.`);
                    return;
                }
                const compIngredients = (comp.ingredients || []).filter(ing => ing && ing.trim());
                if (compIngredients.length === 0) {
                    setError(`Component "${comp.name}" needs at least one ingredient.`);
                    return;
                }
                const compSteps = (comp.steps || []).filter(step => {
                    if (typeof step === 'string') return step.trim();
                    if (step && step.text) return step.text.trim();
                    return false;
                });
                if (compSteps.length === 0) {
                    setError(`Component "${comp.name}" needs at least one step.`);
                    return;
                }
            }
            
            const assemblySteps = (assembly?.steps || []).filter(step => {
                if (typeof step === 'string') return step.trim();
                if (step && step.text) return step.text.trim();
                return false;
            });
            if (assemblySteps.length === 0) {
                setError('Please add at least one assembly step to combine the components.');
                return;
            }
        } else {
            // Simple recipe validation
        const validIngredients = ingredients.filter(ing => ing.trim());
        if (validIngredients.length === 0) {
            setError('Please add at least one ingredient.');
            return;
        }
        
        const validSteps = steps.filter(step => step.trim());
        if (validSteps.length === 0) {
            setError('Please add at least one cooking step.');
            return;
            }
        }

        try {
            if (!currentUser) throw new Error('User not authenticated');
            const token = await currentUser.getIdToken();

            const submitData = new FormData();
            submitData.append('name', name);
            submitData.append('category', category);
            submitData.append('cooking_time', cooking_time);
            submitData.append('isMultiComponent', isMultiComponent);
            submitData.append('originalRecipeId', id);

            if (isMultiComponent) {
                // Multi-component submission
                submitData.append('components', JSON.stringify(components));
                submitData.append('assembly', JSON.stringify(assembly));
                submitData.append('instructions', JSON.stringify({ prerequisites: [], steps: [] }));
                submitData.append('ingredients', JSON.stringify([]));
            } else {
                // Simple recipe submission
                const validIngredients = ingredients.filter(ing => ing.trim());
                const validPrerequisites = prerequisites.filter(pre => pre.trim());
                const validSteps = steps.filter(step => step.trim());

            const instructions = {
                prerequisites: validPrerequisites.map(p => p.trim()).filter(Boolean),
                steps: validSteps.map(s => s.trim()).filter(Boolean)
            };

            const stepsWithTiming = validSteps.map((stepText, index) => {
                const segments = stepTimingSegments[index] || [];
                return {
                    text: stepText,
                    timing_segments: segments.length > 0 ? segments : null
                };
            });

            submitData.append('instructions', JSON.stringify(instructions));
            submitData.append('ingredients', JSON.stringify(validIngredients));
            submitData.append('steps_with_timing', JSON.stringify(stepsWithTiming));
            }

            // Add source URL and author if recipe was transferred from AI
            if (sourceUrl && aiGenerated) {
                submitData.append('ai_source_url', sourceUrl);
                submitData.append('source_url', sourceUrl);
            }
            
            // Add author if available
            if (formData.author) {
                submitData.append('author', formData.author);
            }

            imageFiles.forEach((image) => {
                submitData.append('images', image);
            });

            await forkRecipe(id, submitData, token);
            navigate('/recipes');
        } catch (err) {
            console.error('Error forking recipe:', err);
            setError('Failed to fork recipe. Please try again.');
        }
    };

    const handleAIGeneration = async () => {
        // Validation based on mode
        if (transferMode === 'url' && !urlContext.trim()) {
            setError('Please enter a valid recipe URL to transfer.');
            return;
        }
        
        if (transferMode === 'text' && !textContent.trim()) {
            setError('Please paste recipe content to transfer.');
            return;
        }

        if (!currentUser) {
            setError('You must be logged in to use AI recipe transfer.');
            return;
        }

        // Check acknowledgment
        if (!acknowledgmentAccepted) {
            setError('Please confirm the acknowledgment before transferring.');
            return;
        }

        // For paste mode, show attribution modal first
        if (transferMode === 'text') {
            setShowPasteAttributionModal(true);
            return;
        }

        // Continue with URL mode
        await performTransfer();
    };

    const performTransfer = async () => {
        try {
            setIsGenerating(true);
            setError('');
            setProgressData({ step: '', message: 'Starting recipe transfer...', progress: 0 });

            const token = await currentUser.getIdToken();
            
            // Prepare user recipe data
            const userRecipeData = {
                name: formData.name || '',
                category: formData.category || '',
                cooking_time: parseInt(formData.cooking_time) || 0,
                ingredients: formData.ingredients.filter(item => item.trim() !== ''),
                prerequisites: formData.prerequisites.filter(item => item.trim() !== ''),
                steps: formData.steps.filter(item => item.trim() !== '')
            };

            let result;
            let extractedSourceUrl = '';

            if (transferMode === 'url') {
                result = await generateRecipeWithAI({
                    userRecipeData,
                    url: urlContext
                }, token, (progress) => {
                    setProgressData(progress);
                });
                extractedSourceUrl = result.sourceUrl || urlContext;
            } else {
                result = await generateRecipeWithText({
                    userRecipeData,
                    textContent: textContent
                }, token, (progress) => {
                    setProgressData(progress);
                });
                // Use the URL and author from attribution modal
                extractedSourceUrl = pasteSourceUrl || '';
            }

            const enhancedRecipe = result.recipe || result;
            
            // For pasted content, override author with manual input if provided
            if (transferMode === 'text' && pasteAuthor) {
                enhancedRecipe.author = pasteAuthor;
            }

            // Update form with enhanced data
            const newFormData = {
                ...formData,
                name: enhancedRecipe.name || formData.name,
                category: enhancedRecipe.category || formData.category,
                cooking_time: enhancedRecipe.cooking_time || formData.cooking_time,
                ingredients: enhancedRecipe.ingredients && enhancedRecipe.ingredients.length > 0 
                    ? enhancedRecipe.ingredients 
                    : (formData.ingredients[0] ? formData.ingredients : ['']),
                prerequisites: enhancedRecipe.instructions?.prerequisites && enhancedRecipe.instructions.prerequisites.length > 0
                    ? enhancedRecipe.instructions.prerequisites
                    : (formData.prerequisites[0] ? formData.prerequisites : ['']),
                steps: enhancedRecipe.instructions?.steps && enhancedRecipe.instructions.steps.length > 0
                    ? enhancedRecipe.instructions.steps
                    : (formData.steps[0] ? formData.steps : ['']),
                author: enhancedRecipe.author || null,
                source_url: extractedSourceUrl || null
            };

            // Track which fields were changed
            const fieldsChanged = {
                name: enhancedRecipe.name && enhancedRecipe.name !== formData.name,
                category: enhancedRecipe.category && enhancedRecipe.category !== formData.category,
                cooking_time: enhancedRecipe.cooking_time && enhancedRecipe.cooking_time !== formData.cooking_time,
                ingredients: enhancedRecipe.ingredients && JSON.stringify(enhancedRecipe.ingredients) !== JSON.stringify(formData.ingredients),
                prerequisites: enhancedRecipe.instructions?.prerequisites && JSON.stringify(enhancedRecipe.instructions.prerequisites) !== JSON.stringify(formData.prerequisites),
                steps: enhancedRecipe.instructions?.steps && JSON.stringify(enhancedRecipe.instructions.steps) !== JSON.stringify(formData.steps)
            };

            setFormData(newFormData);
            setHighlightFields(fieldsChanged);
            setAiGenerated(true);
            setSourceUrl(extractedSourceUrl);
            
            // Reset acknowledgment after successful transfer
            setAcknowledgmentAccepted(false);
            setError('');
            
            // Show author modal if no author was detected (but not for paste mode since we already asked)
            if (!enhancedRecipe.author && transferMode !== 'text') {
                setShowAuthorModal(true);
            }

        } catch (err) {
            console.error('❌ Error generating recipe with AI:', err);
            
            let errorMessage = err.message || 'Failed to transfer recipe. Please try again.';
            
            // Show special modal for access denied error
            if (err.message.includes('Access denied')) {
                setShowAccessDeniedModal(true);
                errorMessage = 'Access denied. The website is blocking automated requests.';
            }
            
            setError(errorMessage);
        } finally {
            setIsGenerating(false);
            setProgressData({ step: '', message: '', progress: 0 });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading recipe to fork...</Text>
                    </div>
                </div>
            </div>
        );
    }

    const allImages = [...existingImages, ...imagePreviews];
    const currentImage = allImages[currentImageIndex];

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle={`CREATING VARIATION OF "${originalRecipe?.name || 'RECIPE'}"`}
                >
                    FORK RECIPE
                </PageHeading>

                {/* Toast Error Messages */}
                {error && (
                    <Toast
                        message={error}
                        type="error"
                        onClose={() => setError('')}
                        duration={5000}
                    />
                )}

                {/* Main Recipe Card - Unified Layout */}
                <div className="w-full flex justify-center px-4">
                    <div className="w-full max-w-[630px] md:max-w-[1000px] relative group">
                        <div className="bg-neutral-0 hairline p-6 relative z-10 transition-all duration-200 overflow-y-auto min-h-[95vh] sm:min-h-[600px] sm:max-h-[90vh]">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                            {/* Left Section - Text Content */}
                            <div className="h-full relative overflow-y-auto no-scrollbar">
                                <div className="space-y-6">
                                    {/* Recipe Header - Notecard Style */}
                                    <div className="mb-4">
                                        {/* Recipe Title - Typewriter font, bottom left aligned */}
                                        <div className="text-left mb-3">
                                            <div className="relative">
                                                {editingField === 'name' ? (
                                                    <input
                                                        type="text"
                                                        value={formData.name}
                                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        onBlur={stopEditing}
                                                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                                                        className="w-full text-4xl font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1"
                                                        style={{ color: colorTokens.oxfordBlue }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <h1 
                                                        className="text-4xl font-mono tracking-tight mb-2 cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                        style={{ color: colorTokens.oxfordBlue }}
                                                        onClick={() => startEditing('name')}
                                                    >
                                                        {formData.name || 'Click to add recipe name'}
                                                    </h1>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Red dividing line */}
                                        <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                        
                                        {/* Author info - centered */}
                                        <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                            <span>FORKED RECIPE</span>
                                            <span>•</span>
                                            <span>BY {currentUser?.displayName || 'YOU'}</span>
                                        </div>
                                    </div>

                                    {/* Recipe Information */}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">TIME</div>
                                                <div className="relative">
                                                    {editingField === 'cooking_time' ? (
                                                        <input
                                                            type="number"
                                                            value={formData.cooking_time}
                                                            onChange={(e) => handleFieldChange('cooking_time', e.target.value)}
                                                            onBlur={stopEditing}
                                                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                                                            className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="font-mono text-lg cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                            onClick={() => startEditing('cooking_time')}
                                                        >
                                                            {formData.cooking_time ? formatCookingTime(formData.cooking_time) : 'Click to add time'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">CATEGORY</div>
                                                <div className="relative">
                                                    {editingField === 'category' ? (
                                                        <select
                                                            value={formData.category}
                                                            onChange={(e) => handleFieldChange('category', e.target.value)}
                                                            onBlur={stopEditing}
                                                            className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                            autoFocus
                                                        >
                                                            <option value="">Select category</option>
                                                            {categories.map(cat => (
                                                                <option key={cat} value={cat}>{cat}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <div 
                                                            className="font-mono text-lg cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                            onClick={() => startEditing('category')}
                                                        >
                                                            {formData.category || 'Click to select category'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">ORIGINAL</div>
                                                <div className="font-mono text-lg">
                                                    {originalRecipe?.name || 'Unknown'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Multi-Component Toggle */}
                                    <div className="bg-neutral-50 p-4 hairline">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">RECIPE TYPE</div>
                                                <Text className="text-sm opacity-60">
                                                    {formData.isMultiComponent 
                                                        ? 'Multi-component recipe with separate parts' 
                                                        : 'Simple recipe with a single list of ingredients and steps'}
                                                </Text>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={toggleMultiComponent}
                                                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                    formData.isMultiComponent
                                                        ? 'bg-neutral-900 text-neutral-0'
                                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                }`}
                                            >
                                                {formData.isMultiComponent ? 'MULTI-COMPONENT' : 'SIMPLE'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Conditional Rendering based on isMultiComponent */}
                                    {formData.isMultiComponent ? (
                                        <>
                                            {/* Multi-Component Mode */}
                                            <div>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900">COMPONENTS</div>
                                                    <button
                                                        type="button"
                                                        onClick={addComponent}
                                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                                    >
                                                        + ADD COMPONENT
                                                    </button>
                                                </div>
                                                <Text className="mb-4 opacity-60 text-sm">
                                                    Each component has its own ingredients, prerequisites, and steps.
                                                </Text>
                                                
                                                {formData.components.map((component, index) => (
                                                    <RecipeComponentEditor
                                                        key={component.id || index}
                                                        component={component}
                                                        onChange={(updated) => updateComponent(index, updated)}
                                                        onRemove={formData.components.length > 1 ? () => removeComponent(index) : null}
                                                        accentColor={index % 2 === 0 ? colorTokens.notecardBlue : colorTokens.oxfordBlue}
                                                    />
                                                ))}
                                            </div>

                                            {/* Section Separator */}
                                            <div className="border-t" style={{ borderColor: colorTokens.red }}></div>

                                            {/* Assembly Section */}
                                            <AssemblyEditor
                                                assembly={formData.assembly}
                                                onChange={updateAssembly}
                                                componentNames={formData.components.map(c => c.name).filter(Boolean)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {/* Simple Mode - Original UI */}
                                    {/* Ingredients */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INGREDIENTS</div>
                                        {formData.ingredients && formData.ingredients.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {formData.ingredients.map((ingredient, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                            {index + 1}.
                                                        </span>
                                                        <div className="flex-1">
                                                            {editingField === `ingredient_${index}` ? (
                                                                <textarea
                                                                    value={ingredient}
                                                                    onChange={(e) => handleListChange('ingredients', index, e.target.value)}
                                                                    onBlur={stopEditing}
                                                                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                    className="w-full text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                    style={{ 
                                                                        height: 'auto',
                                                                        minHeight: '20px'
                                                                    }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                    onClick={() => startEditing(`ingredient_${index}`)}
                                                                >
                                                                    {ingredient || 'Click to add ingredient'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {formData.ingredients.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeListItem('ingredients', index)}
                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => addListItem('ingredients')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD INGREDIENT
                                        </button>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Prerequisites */}
                                    {formData.prerequisites && formData.prerequisites.length > 0 && formData.prerequisites[0] && (
                                        <>
                                            <div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                                                <div className="space-y-2">
                                                    {formData.prerequisites.map((prerequisite, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                {index + 1}.
                                                            </span>
                                                            <div className="flex-1 flex items-center gap-2">
                                                                {editingField === `prerequisite_${index}` ? (
                                                                    <textarea
                                                                        value={prerequisite}
                                                                        onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                                                        onBlur={stopEditing}
                                                                        onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                        className="flex-1 text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                        style={{ 
                                                                            height: 'auto',
                                                                            minHeight: '20px'
                                                                        }}
                                                                        onInput={(e) => {
                                                                            e.target.style.height = 'auto';
                                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                                        }}
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="flex-1 text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                        onClick={() => startEditing(`prerequisite_${index}`)}
                                                                    >
                                                                        {prerequisite || 'Click to add prerequisite'}
                                                                    </div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeListItem('prerequisites', index)}
                                                                    className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors flex-shrink-0"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => addListItem('prerequisites')}
                                                    className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                                >
                                                    + ADD PREREQUISITE
                                                </button>
                                            </div>
                                            <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>
                                        </>
                                    )}

                                    {/* Instructions */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INSTRUCTIONS</div>
                                        {formData.steps && formData.steps.length > 0 ? (
                                            <div className="space-y-3">
                                                {formData.steps.map((step, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                            {index + 1}
                                                        </span>
                                                        <div className="flex-1">
                                                            {editingField === `step_${index}` ? (
                                                                <textarea
                                                                    value={step}
                                                                    onChange={(e) => handleListChange('steps', index, e.target.value)}
                                                                    onBlur={stopEditing}
                                                                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                    className="w-full text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                    style={{ 
                                                                        height: 'auto',
                                                                        minHeight: '20px'
                                                                    }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                    onClick={() => startEditing(`step_${index}`)}
                                                                >
                                                                    {step || 'Click to add step'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {formData.steps.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeListItem('steps', index)}
                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                        {/* Active/Inactive Timer Display */}
                                                        <div className="flex items-center ml-2 h-8">
                                                            {(() => {
                                                                const segments = stepTimingSegments[index] || [];
                                                                const activeSegment = segments.find(s => s.kind === 'active');
                                                                const inactiveSegment = segments.find(s => s.kind === 'inactive');
                                                                const hasTimers = (activeSegment?.expectedSeconds || 0) > 0 || (inactiveSegment?.expectedSeconds || 0) > 0;
                                                                
                                                                if (hasTimers) {
                                                                    return (
                                                                        <div className="flex items-center gap-1">
                                                                            {activeSegment && activeSegment.expectedSeconds > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                                    style={{
                                                                                        backgroundColor: '#fee2e2',
                                                                                        border: '1px solid #fca5a5',
                                                                                        color: '#991b1b'
                                                                                    }}
                                                                                    title={activeSegment.label || 'Active time'}
                                                                                >
                                                                                    <TimerIcon size={14} className="mr-1" />
                                                                                    {Math.floor(activeSegment.expectedSeconds / 60)}m
                                                                                </button>
                                                                            )}
                                                                            {inactiveSegment && inactiveSegment.expectedSeconds > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                                    style={{
                                                                                        backgroundColor: '#dbeafe',
                                                                                        border: '1px solid #93c5fd',
                                                                                        color: '#1e40af'
                                                                                    }}
                                                                                    title={inactiveSegment.label || 'Inactive time'}
                                                                                >
                                                                                    <TimerIcon size={14} className="mr-1" />
                                                                                    {Math.floor(inactiveSegment.expectedSeconds / 60)}m
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                // Show add timer icon for steps without timers
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        className="ml-2 flex items-center px-2 py-1 bg-neutral-50 text-neutral-600 hairline hover:bg-neutral-100 transition-colors h-8 text-xs font-mono"
                                                                        onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: [] })}
                                                                        title="Add timers"
                                                                    >
                                                                        <TimerIcon size={16} />
                                                                    </button>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-neutral-900 opacity-60 text-sm">No instructions provided.</div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => addListItem('steps')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD STEP
                                        </button>
                                    </div>
                                        </>
                                    )}
                                    {/* End of multi-component / simple recipe conditional */}

                                    {/* Fade effect overlay */}
                                    <div className="sticky bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-neutral-0 to-transparent pointer-events-none z-10"></div>
                                </div>
                            </div>

                            {/* Right Section - Image Management */}
                            <div className="h-full overflow-hidden h-[40vh] sm:h-full overflow-y-auto">
                                <div className="h-full flex flex-col">
                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">RECIPE IMAGES</div>
                                    
                                    {/* Image Grid */}
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {/* Image Placeholders/Previews */}
                                            {allImages.map((image, index) => (
                                                <div key={index} className="relative group aspect-square">
                                                    <div className="w-full h-full overflow-hidden hairline bg-neutral-50">
                                                        {index < existingImages.length ? (
                                                            <SignedImage
                                                                src={image}
                                                                alt={`Recipe image ${index + 1}`}
                                                                className="w-full h-full object-cover"
                                                                category={formData.category}
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={image} 
                                                                alt={`Preview ${index + 1}`} 
                                                                className="w-full h-full object-cover"
                                                            />
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (index < existingImages.length) {
                                                                removeExistingImage(image);
                                                            } else {
                                                                removeImage(index - existingImages.length);
                                                            }
                                                        }}
                                                        className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Remove image"
                                                    >
                                                        ✕
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            // Replace image at this index
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = (e) => {
                                                                if (e.target.files && e.target.files.length > 0) {
                                                                    const file = e.target.files[0];
                                                                    // Validate file
                                                                    if (!file.type.startsWith('image/')) {
                                                                        setError('Please select a valid image file');
                                                                        return;
                                                                    }
                                                                    if (file.size > 10 * 1024 * 1024) {
                                                                        setError('File is too large (max 10MB)');
                                                                        return;
                                                                    }
                                                                    // Replace image
                                                                    if (index < existingImages.length) {
                                                                        // Replace existing image
                                                                        setExistingImages(prev => {
                                                                            const newImages = [...prev];
                                                                            newImages[index] = URL.createObjectURL(file);
                                                                            return newImages;
                                                                        });
                                                                    } else {
                                                                        // Replace new image
                                                                        const previewIndex = index - existingImages.length;
                                                                        const reader = new FileReader();
                                                                        reader.onload = (e) => {
                                                                            setImagePreviews(prev => {
                                                                                const newPreviews = [...prev];
                                                                                newPreviews[previewIndex] = e.target.result;
                                                                                return newPreviews;
                                                                            });
                                                                        };
                                                                        reader.readAsDataURL(file);
                                                                        setImageFiles(prev => {
                                                                            const newFiles = [...prev];
                                                                            newFiles[previewIndex] = file;
                                                                            return newFiles;
                                                                        });
                                                                    }
                                                                }
                                                            };
                                                            input.click();
                                                        }}
                                                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Replace image"
                                                    >
                                                        <span className="text-white font-mono text-xs uppercase tracking-wider">REPLACE</span>
                                                    </button>
                                                </div>
                                            ))}
                                            
                                            {/* Add Image Placeholder - Clickable Square */}
                                            {allImages.length < 10 && (
                                                <div 
                                                    className={`relative aspect-square hairline cursor-pointer transition-colors flex items-center justify-center group ${
                                                        dragOver ? 'bg-neutral-200' : 'bg-neutral-50 hover:bg-neutral-100'
                                                    }`}
                                                    onClick={triggerFileInput}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                >
                                                    <div className="text-center">
                                                        <div className="text-3xl mb-2 opacity-60 group-hover:opacity-80">+</div>
                                                        <div className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 group-hover:opacity-80">
                                                            ADD IMAGE
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Hidden File Input */}
                                        <input
                                            id="fork-image-input"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileInput}
                                            className="hidden"
                                        />
                                        
                                        {allImages.length > 0 && (
                                            <div className="mt-3 text-xs text-neutral-600 font-mono uppercase tracking-wider">
                                                Hover over images to edit or remove
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="max-w-5xl mx-auto mt-6">
                    <div className="flex justify-center items-center space-x-4">
                        <form onSubmit={handleSubmit} className="contents">
                            <button
                                type="submit"
                                className="px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                FORK RECIPE
                            </button>
                        </form>
                        <button
                            type="button"
                            onClick={() => navigate(`/recipes/${id}`)}
                            className="px-6 py-3 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Author Input Modal */}
            <Modal
                isOpen={showAuthorModal}
                onClose={() => setShowAuthorModal(false)}
                title="Recipe Author"
            >
                <div className="vspace">
                    <Text>
                        We couldn't automatically detect the original recipe author. Please enter the author's name to give proper credit.
                    </Text>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Author Name
                            </label>
                            <input
                                type="text"
                                value={manualAuthor}
                                onChange={(e) => setManualAuthor(e.target.value)}
                                placeholder="Enter the original recipe author's name"
                                className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                        </div>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={() => {
                                    if (manualAuthor.trim()) {
                                        setFormData(prev => ({ ...prev, author: manualAuthor.trim() }));
                                    }
                                    setShowAuthorModal(false);
                                }}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                SAVE AUTHOR
                            </button>
                            <button
                                onClick={() => setShowAuthorModal(false)}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                SKIP
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Paste Attribution Modal - shown before transfer starts */}
            <Modal
                isOpen={showPasteAttributionModal}
                onClose={() => setShowPasteAttributionModal(false)}
                title="Recipe Attribution"
            >
                <div className="vspace">
                    <Text className="mb-4">
                        To properly credit this recipe as a transferred recipe, please provide the following information:
                    </Text>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Original Recipe Author *
                            </label>
                            <input
                                type="text"
                                value={pasteAuthor}
                                onChange={(e) => setPasteAuthor(e.target.value)}
                                placeholder="e.g., Gordon Ramsay, Bon Appétit, etc."
                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                            <Text size="xs" className="text-neutral-900 opacity-60 mt-1">
                                Who created this recipe?
                            </Text>
                        </div>

                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Source URL (Optional)
                            </label>
                            <input
                                type="url"
                                value={pasteSourceUrl}
                                onChange={(e) => setPasteSourceUrl(e.target.value)}
                                placeholder="https://example.com/recipe"
                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                            <Text size="xs" className="text-neutral-900 opacity-60 mt-1">
                                Where did you find this recipe? (helps give proper credit)
                            </Text>
                        </div>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={async () => {
                                    if (!pasteAuthor.trim()) {
                                        setError('Please enter the recipe author name to continue.');
                                        return;
                                    }
                                    setShowPasteAttributionModal(false);
                                    await performTransfer();
                                }}
                                disabled={!pasteAuthor.trim()}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ✨ START TRANSFER
                            </button>
                            <button
                                onClick={() => {
                                    setShowPasteAttributionModal(false);
                                    setPasteAuthor('');
                                    setPasteSourceUrl('');
                                }}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Access Denied Modal */}
            <Modal
                isOpen={showAccessDeniedModal}
                onClose={() => setShowAccessDeniedModal(false)}
                title="Website Blocking Access"
            >
                <div className="vspace">
                    <div className="bg-yellow-50 p-4 hairline border-l-4 border-l-yellow-400 mb-4">
                        <Text className="text-yellow-900 font-mono text-xs uppercase tracking-wider mb-2">
                            ⚠️ WEBSITE DOES NOT ALLOW SCRAPING
                        </Text>
                        <Text size="sm" className="text-yellow-900">
                            Oops! This website is blocking automated requests and won't let us extract the recipe directly.
                        </Text>
                    </div>
                    
                    <div className="bg-green-50 p-4 hairline border-l-4 border-l-green-400 mb-4">
                        <Text className="text-green-900 font-mono text-xs uppercase tracking-wider mb-2">
                            ✨ BUT YOU CAN STILL COPY & PASTE!
                        </Text>
                        <Text size="sm" className="text-green-900 mb-3">
                            Simply copy the recipe content from the website and paste it directly here. Our AI will extract the ingredients and instructions for you.
                        </Text>
                        <ol className="text-green-900 text-xs space-y-2 ml-4 list-decimal">
                            <li>Open the recipe website in your browser</li>
                            <li>Select and copy all the recipe content (ingredients, instructions, etc.)</li>
                            <li>Return here and click the <strong>"📋 PASTE TEXT"</strong> tab above</li>
                            <li>Paste the content and click <strong>"✨ TRANSFER"</strong></li>
                        </ol>
                    </div>
                    
                    <div className="flex space-x-4 pt-4">
                        <button
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                setTransferMode('text');
                            }}
                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            📋 SWITCH TO PASTE MODE
                        </button>
                        <button
                            onClick={() => setShowAccessDeniedModal(false)}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            CLOSE
                        </button>
                    </div>
                </div>
            </Modal>
            
            <TimerEditModal
                open={timerModal.open}
                onClose={() => setTimerModal({ open: false })}
                onSave={(newSegments) => {
                    setStepTimingSegments(prev => ({
                        ...prev,
                        [timerModal.stepIndex]: newSegments
                    }));
                    setTimerModal({ open: false });
                }}
                onRemove={() => {
                    setStepTimingSegments(prev => {
                        const updated = { ...prev };
                        delete updated[timerModal.stepIndex];
                        return updated;
                    });
                    setTimerModal({ open: false });
                }}
                stepIndex={timerModal.stepIndex}
                timingSegments={timerModal.timingSegments}
            />
        </div>
    );
};

export default ForkRecipe;
